import { DrawingProperties } from '@cadstrom/renderer';
import clsx from 'clsx';
import { memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { CadRendererCanvas } from './cad-renderer-canvas.js';
import { DEFAULT_MODEL_GRID, ZOOM_PADDING_FRACTION } from './constants.js';
import { ActiveDesignContext } from './context/active-design-context.js';
import { DrawingManagerContext } from './context/drawing-manager-context.js';
import { useInteractionManagerContext } from './context/interaction-manager-context.js';
import { ZIndexProvider } from './context/z-index-context.js';
import { ModuleHoverInfo } from './hover-info/module-hover-info.js';
import { CadRendererStatusBar } from './status-bar/cad-renderer-status-bar.js';
import { useCadRendererDrawingManagerSync } from './sync/cad-renderer-sync-context.js';
import { CadRendererToolbar } from './toolbar/cad-renderer-toolbar.js';

import type { DrawingManager } from '@cadstrom/renderer';
import type { Design } from '@cadstrom/sigma-engine-shared';
import type { ReactNode, Ref } from 'react';
import type { ViewportState } from '#src/utils/events.js';
import type { CadRendererCanvasProps } from './cad-renderer-canvas.js';
import type { ActiveDesignContextValue } from './context/active-design-context.js';
import type { SwitchedComponentProps } from './switchable-views.js';

export type CadRendererInstanceRef = {
	zoomToModule: (moduleRef: string) => void;
	/** Returns the current viewport centre and scale, or null if the canvas is not yet ready. */
	getViewportState: () => ViewportState | null;
	/** Restores the viewport to the given state. Queues the restore if the canvas is not yet ready. */
	restoreViewport: (state: ViewportState) => void;
};

export type CadRendererInstanceOwnProps = {
	/** The design this instance renders — not necessarily the app-wide active design. */
	design: Design;
	ref?: Ref<CadRendererInstanceRef | null>;
	onOpenInNewWindow?: () => void;

	cadRendererCanvasProps: CadRendererCanvasProps;

	/**
	 * Rendered in a full-bleed, pointer-transparent layer over the canvas area
	 * (excluding the toolbar and status bar). Children position themselves with
	 * absolute placement; interactive children must opt back in with
	 * `pointer-events-auto`.
	 */
	canvasOverlay?: ReactNode;

	/** Hides the tools column beside the canvas. */
	hideToolbar?: boolean;
	/** Hides the status bar below the canvas. */
	hideStatusBar?: boolean;
};

export type CadRendererInstanceProps = SwitchedComponentProps & CadRendererInstanceOwnProps;

export const CadRendererInstance = memo(function CadRendererInstance({
	visible,
	design,
	ref,
	onOpenInNewWindow,
	cadRendererCanvasProps,
	canvasOverlay,
	hideToolbar = false,
	hideStatusBar = false,
}: CadRendererInstanceProps) {
	const { interactionManager } = useInteractionManagerContext();
	const { registerDrawingManager } = useCadRendererDrawingManagerSync();
	const [drawingManager, setDrawingManager] = useState<DrawingManager | null>(null);

	const zoomToModuleRef = useRef<string | undefined>(undefined);
	const pendingViewportRestoreRef = useRef<ViewportState | null>(null);

	const applyViewportState = useCallback((dm: DrawingManager, state: ViewportState) => {
		const viewport = dm.renderer.getViewport();
		viewport.scaled = state.scale;
		viewport.moveCenter(state.centerX, state.centerY);
	}, []);

	// Apply any pending viewport restore once the drawing manager becomes available
	useEffect(() => {
		if (drawingManager && !drawingManager.isDestroyed && pendingViewportRestoreRef.current !== null) {
			applyViewportState(drawingManager, pendingViewportRestoreRef.current);
			pendingViewportRestoreRef.current = null;
		}
	}, [applyViewportState, drawingManager]);

	const handleZoomToModule = useCallback(
		(drawingManager: DrawingManager) => {
			if (zoomToModuleRef.current === undefined || !visible) {
				return;
			}

			interactionManager.selectElementsByProperty(DrawingProperties.ObjectId, zoomToModuleRef.current, false);
			drawingManager.zoomToSelection(ZOOM_PADDING_FRACTION);
		},
		[interactionManager, visible],
	);

	useImperativeHandle(
		ref,
		() => ({
			zoomToModule: (moduleRef: string) => {
				zoomToModuleRef.current = moduleRef;
				if (drawingManager) {
					handleZoomToModule(drawingManager);
				}
			},

			getViewportState: () => {
				if (!drawingManager || drawingManager.isDestroyed) {
					return null;
				}
				const viewport = drawingManager.renderer.getViewport();
				return { centerX: viewport.center.x, centerY: viewport.center.y, scale: viewport.scaled };
			},

			restoreViewport: (state: ViewportState) => {
				if (!drawingManager || drawingManager.isDestroyed) {
					pendingViewportRestoreRef.current = state;
					return;
				}
				applyViewportState(drawingManager, state);
			},
		}),
		[applyViewportState, drawingManager, handleZoomToModule],
	);

	useEffect(() => {
		const isTickerRunning = drawingManager?.renderer.isTickerRunning();
		if (visible && !isTickerRunning) {
			drawingManager?.renderer.resumeTicker();
		} else if (!visible && isTickerRunning) {
			drawingManager?.renderer.pauseTicker();
		}
	}, [drawingManager?.renderer, visible]);

	useEffect(() => {
		if (!drawingManager || drawingManager.isDestroyed) {
			return;
		}

		return registerDrawingManager({
			designId: design.designId,
			layerSet: design.layerSet,
			modelType: design.modelType,
			modelGrid: design.grid ?? DEFAULT_MODEL_GRID,
			drawingManager,
		});
	}, [design.designId, design.grid, design.layerSet, design.modelType, drawingManager, registerDrawingManager]);

	const drawingManagerContextValue = useMemo(
		() => ({
			drawingManager,
			setDrawingManager,
		}),
		[drawingManager, setDrawingManager],
	);

	// Shadows the page-level ActiveDesignContext, so that an instance's descendants resolve this instance's design,
	// not whichever design is app-wide active.
	const activeDesignContextValue = useMemo<ActiveDesignContextValue>(() => ({ activeDesign: design }), [design]);

	return (
		<ActiveDesignContext value={activeDesignContextValue}>
			<DrawingManagerContext value={drawingManagerContextValue}>
				<ZIndexProvider>
					<div className={clsx('flex h-full w-full min-w-0 flex-col', !visible && 'hidden')}>
						<div className='flex min-h-0 min-w-0 grow basis-0'>
							{!hideToolbar && <CadRendererToolbar onOpenInNewWindow={onOpenInNewWindow} />}

							<ModuleHoverInfo visible={visible} className='relative h-full min-w-0 grow'>
								<CadRendererCanvas {...cadRendererCanvasProps} onInitialized={handleZoomToModule} isActive={visible} />
								{canvasOverlay !== undefined && (
									<div className='pointer-events-none absolute inset-0'>{canvasOverlay}</div>
								)}
							</ModuleHoverInfo>
						</div>

						{!hideStatusBar && <CadRendererStatusBar modelGrid={design.grid ?? DEFAULT_MODEL_GRID} />}
					</div>
				</ZIndexProvider>
			</DrawingManagerContext>
		</ActiveDesignContext>
	);
});
